import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useState } from 'react';
import { ExternalLink, ShieldCheck, AlertTriangle, RotateCcw, CreditCard, CalendarClock,
  Receipt, FileText,
} from 'lucide-react';
import { journeyApi, type ManualCompletions } from '../../services/journeyApi';
import { openTenantInvoice, type InvoiceCharge } from '../../utils/tenantInvoicePrint';
import { billingApi, type PaymentMethodsResult, type SoldLocation } from '../../services/billingApi';
import { dealsApi } from '../../services/dealsApi';
import SubscriptionVerifyList from './SubscriptionVerifyList';
import { invoiceLocationId } from './locationBilling';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import { useDataVersion } from '../../services/dataChangeBus';
import type { DeviceUnitRow } from '../../services/hardwareApi';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * PAYMENT VERIFIED — a person confirming the money actually landed.
 *
 * Stripe saying "paid" and somebody having LOOKED are different facts, and
 * early on the second one matters: a card can be authorised and reversed, a
 * test key can look exactly like a live one, and an order picked against a
 * payment nobody checked is stock out of the door for nothing.
 *
 * So this stage completes manually, and what it shows first is everything
 * needed to make that judgement — what is due, what the device schedule
 * commits them to, what the subscription costs, and a link straight into
 * Stripe for the ledger this screen does not own.
 */

const money = (cents?: number) =>
  typeof cents === 'number' ? `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—';

const when = (ts?: number) =>
  ts ? new Date(ts).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

interface Props {
  journeyId: string | null;
  tenantId: string;
  orderId: string;
  /**
   * The units on THIS order. Each financed one carries its own agreement —
   * term, monthly and principal — which IS the device payment schedule for
   * this order, and is already on the screen. No second source to disagree
   * with.
   */
  units: DeviceUnitRow[];
  /**
   * DEPRECATED as a figure to SHOW, kept so the prop is not removed from a
   * caller. It sums every line's `amountCents` whatever its sale mode, so a
   * rental's MONTHLY rate is added to a purchase PRICE and the total is
   * neither: on the order that exposed this, $630.00 of outright hardware plus
   * $247.97/mo of rentals came out as "$877.97 due", a number nobody is ever
   * charged. `orderItems` is split by sale mode below instead.
   */
  dueCents?: number;
  /** The order's own lines, split by sale mode below — see `dueCents`. */
  orderItems?: Array<{
    skuId?: string;
    quantity?: number;
    amountCents?: number;
    saleMode?: string;
    termMonths?: number;
  }>;
  completion?: ManualCompletions[string];
  /** This order's store — the subscriptions and transactions open on it. */
  locationId?: string;
  /** The subscription this order started, when it is an add-on order. */
  addOnSubscriptionId?: string;
  onChanged: (manual: ManualCompletions) => void;
}

const PaymentVerifyPanel: React.FC<Props> = ({ journeyId, tenantId, units, orderId, orderItems, completion, onChanged, locationId, addOnSubscriptionId }) => {
  /** Transactions show this order's store unless the operator asks for every store. */
  const [txAllStores, setTxAllStores] = useState(false);
  const [plan, setPlan] = useState<any>(null);
  const [tenant, setTenant] = useState<any>(null);
  /** What Stripe actually took — the authoritative figures on this screen. */
  const [charges, setCharges] = useState<InvoiceCharge[] | null>(null);
  /** Package NAMES; the assignment carries only keys. */
  const [packageNames, setPackageNames] = useState<string[]>([]);
  /**
   * The live subscriptions and their real monthly totals, from Stripe's own
   * items. Null when billing-service could not read the customer — which is
   * NOT the same as "no subscriptions", and is said out loud below rather than
   * rendered as zero.
   */
  const [billing, setBilling] = useState<PaymentMethodsResult | null>(null);
  /** `locationId` → the name the store was set up under. Best-effort; see `load`. */
  const [locationNames, setLocationNames] = useState<Record<string, string>>({});
  /** What we SENT to Stripe, per location — the completion's own stored lines. */
  const [soldLocations, setSoldLocations] = useState<SoldLocation[]>([]);
  /** The PINNED deal version this order was sold on — the offer, as contracted. */
  const [dealVersion, setDealVersion] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const dataVersion = useDataVersion();

  /*
    ⚠️ `setLoading(false)` MUST RUN, whatever else happens in here.

    Every read below is individually `.catch`ed, but the code BETWEEN them is
    not: one throw — a mis-shaped response, a helper that is momentarily
    undefined — left `loading` true for ever, and this whole panel renders
    nothing while it is. That took the Verify control off the screen along with
    everything else, on the stage whose entire job is letting a person check
    the money before stock leaves. A `finally` is the difference between a
    degraded panel and no panel.
  */
  const load = useCallback(async () => {
    try {
      await loadInner();
    } finally {
      setLoading(false);
    }
  // dataVersion: re-read in place after a save anywhere on the order screen.
  }, [tenantId, dataVersion]);

  const loadInner = useCallback(async () => {
    // Neither read may hide the verify control: an operator checking Stripe
    // directly is exactly the fallback this stage exists for, and a billing
    // service that is down must not stop them recording what they saw.
    /*
      THE TENANT ROW, NOT THE BUSINESS PROFILE.

      This read `api.tenants.getMetadata`, whose client maps every response
      through `ensureProfile` — a fixed list of presentation fields (name,
      address, phone, owner, hours). `stripeCustomerId` and
      `stripeSubscriptionId` are not on that list, so they were stripped on the
      way through and the panel below said "No Stripe ids on this tenant yet"
      for EVERY tenant, always, however much Stripe held. An operator checking
      whether the money landed was told there was nothing to check.

      `getTenant` returns the tenant row itself, and its `Tenant` type declares
      both ids. Nothing else on this panel reads `tenant`, so this swap changes
      only where the two ids come from.
    */
    const [assignment, row, invoices, pkgs, methods, locReqs, sold] = await Promise.all([
      billingApi.getActivePlanAssignment(tenantId).catch(() => null),
      api.superAdmin.getTenant(tenantId).catch(() => null),
      // The money that actually moved. Every one of these reads is allowed to
      // fail on its own: an operator checking Stripe directly is exactly the
      // fallback this stage exists for, and none of them may hide the control.
      api.superAdmin.getTenantInvoices(tenantId, 20).catch(() => null),
      api.superAdmin.getTenantPackages(tenantId).catch(() => null),
      /*
        THE SUBSCRIPTIONS THEMSELVES, SUMMED FROM THEIR OWN STRIPE ITEMS.

        This panel had no source for the real monthly and fell back to
        `tenant.subscriptionCost` — a single price super-admin stamps from
        `items.data[0]` (see `admin.ts`, `updatePaymentMethod`). Under per-item
        billing a subscription carries one item per package, so that field is
        item ONE OF THIRTEEN: an account billing $165.91 of software reported
        "$39.99" on the screen where somebody decides whether the money landed.

        `listPaymentMethods` already returns `liveSubscriptions[].monthlyCents`
        and `monthlyTotalCents`, both summed from Stripe's items — no new
        endpoint, and one read for both the corrected figure and the rows the
        verify list renders.
      */
      billingApi.listPaymentMethods(tenantId).catch(() => null),
      /*
        WHAT THE STORES ARE CALLED.

        `loc_1788625971118` is an id, and an id is not what an operator calls a
        shop. The location-billing requests carry the name each store was set
        up under, keyed by the same `locationId` the subscriptions are
        attributed to. Best-effort throughout: a store created by the wizard may
        have no request row at all, and a row without a name simply falls back
        to its id rather than blanking the heading.
      */
      api.locationBilling.listTenantRequests(tenantId).catch(() => null),
      /*
        WHAT WE SENT TO STRIPE. The completion's own stored breakdown, per SKU,
        as it was at the moment of sale — the record every figure on this screen
        is supposed to be checked against, and which nothing here could reach
        until billing-service grew an operator route to it.
      */
      billingApi.getOnboardingResult(tenantId).catch(() => null),
    ]);
    setPlan((assignment as any)?.items?.[0] || null);
    setTenant(row || null);
    setCharges((invoices as any)?.invoices || []);
    setPackageNames(((pkgs as any)?.packageDetails || []).map((d: any) => d.name).filter(Boolean));
    setBilling(methods && (methods as any).readable ? methods : null);
    // Both response shapes, because this endpoint has carried each at different
    // times and a name is not worth a crash.
    const reqItems: any[] = (locReqs as any)?.requests ?? (locReqs as any)?.items
      ?? (Array.isArray(locReqs) ? locReqs : []);
    const names: Record<string, string> = {};
    for (const r of reqItems) {
      if (r?.locationId && r?.name) names[r.locationId] = String(r.name);
    }
    setLocationNames(names);
    setSoldLocations((sold as any)?.result?.locations || []);

    /*
      THE OFFER THIS ORDER WAS SOLD ON — the PINNED version, never the live deal.

      A deal is edited over its life; the version the tenant signed is the
      contract, which is why `dealVersionNo` is stamped on the tenant at signup
      and why deals-service keeps every published version. Reading the live deal
      instead reports whatever it says TODAY: for this account that is no
      promotions at all, while Stripe charges two — the same wrong-number
      failure as everything else on this screen, from the other direction.

      Works for any version by construction: the tenant names its own.
    */
    const dealId = (row as any)?.dealId;
    const versionNo = (row as any)?.dealVersionNo;
    if (dealId && typeof versionNo === 'number') {
      dealsApi.getVersion(dealId, versionNo)
        // The WHOLE version: its `packageIds`/`deviceSkuIds` are what separate a
        // deal line from an add-on, and they bill differently.
        .then((v: any) => setDealVersion({ versionNo, ...(v?.version || v) }))
        .catch(() => setDealVersion(null));
    } else {
      setDealVersion(null);
    }
    // `setLoading(false)` lives in `load`'s finally — see the note there.
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const verify = async () => {
    if (!journeyId) return;
    setBusy(true);
    try {
      const res = await journeyApi.complete(journeyId, 'payment_verified', note.trim() || undefined);
      onChanged(res.manual);
      setNote('');
      toastService.success('Payment verified — recorded against your name.');
    } catch (e: any) {
      toastService.error(e?.message || 'Could not record that.');
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    if (!journeyId) return;
    setBusy(true);
    try {
      const res = await journeyApi.undo(journeyId, 'payment_verified', note.trim() || undefined);
      onChanged(res.manual);
      toastService.info('Verification withdrawn. The original still stands on the timeline.');
    } catch (e: any) {
      toastService.error(e?.message || 'Could not record that.');
    } finally {
      setBusy(false);
    }
  };

  const subscriptionId = (tenant as any)?.stripeSubscriptionId;
  const customerId = (tenant as any)?.stripeCustomerId;

  /*
    TEST-MODE OBJECTS LIVE UNDER `/test/`.

    Dev and staging run on `sk_test_` keys, and a link to the live dashboard for
    a test object lands on a "no such customer" page. Derived from the hostname
    so a build cannot be pointed at the wrong ledger by a stale constant.
  */
  const stripeBase = /localhost|dev\.|staging\./.test(window.location.hostname)
    ? 'https://dashboard.stripe.com/test'
    : 'https://dashboard.stripe.com';

  const openInvoice = () => {
    const ok = openTenantInvoice({
      tenantName: (tenant as any)?.name || (tenant as any)?.businessName || tenantId,
      tenantId,
      charges: charges || [],
      monthlyCents: systemMonthlyCents ?? (tenant as any)?.subscriptionCost,
      softwareMonthlyCents,
      hardwareMonthlyCents: systemHardwareMonthlyCents,
      packageNames,
      // Units when the order has been allocated (they carry serials); the order
      // lines before that, so the document is never blank at verify time.
      schedule: schedule.length > 0 ? schedule : rentedLines,
      stripeCustomerId: customerId,
      stripeSubscriptionId: subscriptionId,
      stripeDashboardBase: stripeBase,
    });
    if (!ok) toastService.error('Your browser blocked the invoice window — allow popups for this site.');
  };

  const paidAt = (list: Array<{ status: string; amountPaid?: number }>) => list
    .filter((c) => c.status === 'paid')
    .reduce((n, c) => n + (c.amountPaid || 0), 0);
  const paidCents = paidAt(charges || []);

  /*
    TRANSACTIONS FOR THIS ORDER'S STORE. An invoice is placed by its own
    location, or by the subscription that raised it (a renewal Stripe raises
    carries no location). "All stores" is one tap away.
  */
  const liveSubs = billing?.liveSubscriptions || [];
  const storeNameOf = (c: any) => {
    const loc = invoiceLocationId(c, liveSubs);
    return loc ? (locationNames[loc] || loc) : 'Not linked to a store';
  };
  const txStoreScoped = !!locationId && !txAllStores;
  const homeStoreName = locationId ? (locationNames[locationId] || 'This store') : '';
  const txVisible = txStoreScoped
    ? (charges || []).filter((c: any) => invoiceLocationId(c, liveSubs) === locationId)
    : (charges || []);

  /*
    ONE-TIME AND MONTHLY ARE DIFFERENT UNITS, AND WERE BEING ADDED TOGETHER.

    An order line's `amountCents` means a PRICE on an outright line and a
    MONTHLY RATE on a rental or installment one. The old "Due on this order"
    summed all of them, so an order of $630.00 of bought hardware and
    $247.97/mo of rented hardware displayed "$877.97 due" — a figure that is
    neither what was taken today nor what recurs, and that nobody is charged.

    Split here, and reconciled against Stripe below rather than presented as
    the truth on its own: deposits and fees are NOT order lines, so our
    one-time figure is expected to be lower than the invoice, and saying so
    beats quietly showing a number that disagrees with the bank.
  */
  const lines = orderItems || [];
  const qtyOf = (i: { quantity?: number }) => i.quantity || 1;
  const systemHardwareMonthlyCents = lines
    .filter((i) => i.saleMode && i.saleMode !== 'outright' && i.saleMode !== 'included')
    .reduce((n, i) => n + (i.amountCents || 0) * qtyOf(i), 0);
  /*
    SOFTWARE, FROM THE SUBSCRIPTION'S OWN ITEMS.

    This was `tenant.subscriptionCost`, which super-admin writes as
    `items.data[0].price.unit_amount` — the FIRST item of a subscription that
    now carries one per package. On the order that exposed this it reported
    $39.99, Ruby Core alone, for an account billing $165.91 of software across
    nine packages, and the "RubyTill" column an operator checks against Stripe
    was therefore wrong by $126.

    `monthlyTotalCents` is every live subscription summed from Stripe's items,
    so software is that total less the hardware this order's own lines account
    for. The stale field is kept only as a last resort, and only when Stripe
    could not be read at all — a wrong number is better than a blank column on
    a screen whose whole job is comparison, provided it is the fallback and not
    the source.
  */
  const stripeTotalMonthlyCents = billing?.monthlyTotalCents;
  const softwareMonthlyCents = typeof stripeTotalMonthlyCents === 'number'
    ? Math.max(0, stripeTotalMonthlyCents - systemHardwareMonthlyCents)
    : (tenant as any)?.subscriptionCost;
  const systemMonthlyCents = typeof stripeTotalMonthlyCents === 'number'
    ? stripeTotalMonthlyCents
    : (typeof softwareMonthlyCents === 'number'
      ? softwareMonthlyCents + systemHardwareMonthlyCents
      : undefined);

  /*
    `systemOneTimeCents`, `oneTimeCharged`, `recurringCharge`, `stripeMonthlyCents`
    and `delta` are GONE with the comparison card that was their only reader.
    `recurringCharge` in particular picked "the newest paid invoice carrying a
    period" as the subscription's — which selected the deposits invoice, because
    Stripe stamps a period on those too. Nothing infers that any more:
    `isSubscriptionInvoice` reads Stripe's own billingReason.
  */

  /*
    NAME THE CHARGE, DON'T RECITE STRIPE'S COUNTER.

    "XPAVMYGE-0002" is Stripe's sequence number for its own account. It tells
    an operator nothing about what the money was for, which is the only thing
    they are here to establish.

    The one fact that separates these invoices is whether Stripe recorded a
    BILLING PERIOD on them: a subscription invoice covers a period, the
    charge taken at checkout does not. That is read off the invoice itself
    rather than guessed from the amount, so it stays right when the figures
    change.

    The Stripe number is kept — demoted beside the link, because it is what an
    operator types into Stripe to find this — but it is no longer the headline.
  */
  const shortDay = (ms?: number) =>
    typeof ms === 'number' && ms > 0
      ? new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
      : null;

  /*
    THE RENTED HARDWARE, FROM THE ORDER RATHER THAN THE UNITS.

    `schedule` above is built from units, which carry serial numbers and are
    the better source — but units exist only once the order has been
    ALLOCATED, and payment is verified long before that. On a fresh order the
    schedule is empty, so the monthly charge had nothing behind it. The order
    lines are there from the moment it is placed.
  */
  const rentedLines = lines
    .filter((i) => i.saleMode && i.saleMode !== 'outright' && i.saleMode !== 'included')
    .map((i) => ({
      name: i.skuId || 'device',
      saleMode: i.saleMode,
      termMonths: i.termMonths,
      monthlyAmountCents: (i.amountCents || 0) * qtyOf(i),
      quantity: qtyOf(i),
    }));
  const rentedCount = rentedLines.reduce((n, l) => n + l.quantity, 0);

  /*
    WHICH INVOICE IS WHICH — FROM STRIPE'S OWN REASON, NOT FROM A PERIOD.

    This keyed off `c.periodStart`, on the belief that a subscription invoice
    covers a period and a checkout charge does not. Stripe stamps a period on
    BOTH: the $449.99 deposits-and-down-payments invoice came back with
    Sep 5 – Oct 5 on it and was therefore labelled "Monthly subscription",
    directly above a $250.41 invoice that actually was one. Two invoices with
    the same title and different amounts, on the screen whose entire job is
    telling an operator what the money was for.

    `billingReason` is Stripe's own answer and needs no inference. Absent on a
    completion recorded before super-admin reported it, in which case the old
    period heuristic still applies — wrong in exactly the case it was always
    wrong in, and never worse.
  */
  const isSubscriptionInvoice = (c: InvoiceCharge): boolean =>
    c.billingReason
      ? c.billingReason.startsWith('subscription')
      : !!c.periodStart;

  const chargeLabel = (c: InvoiceCharge): string => {
    if (!isSubscriptionInvoice(c)) return 'Paid at checkout · deposits and down payments';
    const from = shortDay(c.periodStart);
    const to = shortDay(c.periodEnd);
    return from && to ? `Monthly subscription · ${from} – ${to}` : 'Monthly subscription';
  };
  /*
    REMOVED: `plan?.totals?.netCents ?? plan?.lineNetCents`.

    Neither field exists on `PlanAssignmentRow` — it carries `planPackageIds`,
    `discounts` and `locationPackageIds`, and no money at all. So this was
    ALWAYS `undefined`, and the "Subscription, monthly" line it fed simply
    never rendered. The monthly figure now comes from the tenant's own
    `subscriptionCost` plus the rented lines on the order, and is checked
    against what Stripe billed.

    The assignment is still read: it is what tells us the tenant HAS an active
    plan, and its packages are named on the invoice.
  */

  // The device schedule, straight off the units on this order.
  const schedule = units
    .filter((u) => u.agreement && (u.agreement as any).monthlyAmountCents)
    .map((u) => ({
      key: u.unitId,
      name: u.serialNumber || u.trackingId || u.unitId,
      saleMode: (u.agreement as any).saleMode,
      termMonths: (u.agreement as any).termMonths,
      monthlyAmountCents: (u.agreement as any).monthlyAmountCents,
    }));

  return (
    <div className="space-y-3">
      {/*
        THE RUBYTILL-VS-STRIPE CARD IS GONE, not hidden.

        It compared two figures it could not source: "Due today, bought
        outright" summed only order lines whose saleMode is `outright` — none,
        on an order of rentals and instalments — so it read $0.00 beside a
        $449.99 charge; and "Monthly" quoted the settled $302.89 with no offer
        applied, against an account billing $250.41.

        Both questions are answered properly and in one place each now: the
        month-by-month schedule above states what is taken and when, with the
        offers subtracted, and the per-subscription list below reconciles our
        stored lines against Stripe item by item. A third panel restating them
        wrongly is worse than no panel at all.

        (A BARE `/* *\/` HERE IS NOT A COMMENT — JSX renders it as text. It has
        to be wrapped in braces, as this one is.)
      */}

      {/*
        THE REAL CHECK, PER SUBSCRIPTION AND ONLY WHEN ASKED FOR.

        Two independent computations of the same money meet here: billing-service
        summed the items server-side (`monthlyCents`), and pressing Verify pulls
        Stripe's raw subscription and sums them again in the browser. A
        difference between those two is a genuine finding; the block above,
        where both columns would come from the same read, is not.
      */}
      {!loading && billing && billing.liveSubscriptions.length > 0 && (
        <SubscriptionVerifyList
          subscriptions={billing.liveSubscriptions}
          invoices={charges as any}
          locationNames={locationNames}
          businessName={(tenant as any)?.businessName || (tenant as any)?.name}
          soldLocations={soldLocations}
          dealVersion={dealVersion}
          stripeBase={stripeBase}
          homeLocationId={locationId}
          orderSubscriptionId={addOnSubscriptionId}
        />
      )}
      {!loading && !billing && (
        /* Unreadable is NOT empty — an operator told "no subscriptions" would
           release stock against a payment nobody could confirm. */
        <p className="text-[11.5px] text-amber-700 inline-flex items-start gap-1.5">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          We could not read this tenant&apos;s subscriptions from Stripe just now, so the monthly
          above comes from the tenant record. Check Stripe directly before verifying.
        </p>
      )}

      {loading && (
        <RubyLoader variant="inline" label="reading the billing record…" />
      )}

      {/* The device schedule — what they are committed to, month by month */}
      {schedule.length > 0 && (
        <div className="rounded-xl border border-stone-200 p-3.5">
          <p className="text-[11px] uppercase tracking-[0.12em] font-bold text-stone-500 mb-2 inline-flex items-center gap-1.5">
            <CalendarClock size={12} /> Device payment schedule
          </p>
          {schedule.slice(0, 6).map((a) => (
            <div key={a.key} className="flex items-center justify-between gap-3 py-1.5 border-b border-stone-100 last:border-0">
              <span className="text-[12.5px] text-stone-700 truncate font-mono">
                {a.name}
                <span className="text-stone-400 font-sans">
                  {' · '}{a.saleMode}{a.termMonths ? ` · ${a.termMonths} months` : ' · open-ended'}
                </span>
              </span>
              <span className="text-[12.5px] font-semibold text-stone-800 shrink-0">
                {money(a.monthlyAmountCents)}/mo
              </span>
            </div>
          ))}
          {schedule.length > 6 && (
            <p className="text-[11.5px] text-stone-400 mt-1.5">{schedule.length - 6} more</p>
          )}
        </div>
      )}

      {/*
        WHAT ACTUALLY MOVED, and the document that explains it.

        The panel showed what was DUE and left the operator to go and look up
        whether anything was taken. These are the real charges, with the amount
        Stripe reports as paid, so "did the money land" can be answered here.
      */}
      {(charges?.length ?? 0) > 0 && (
        <div className="rounded-xl border border-stone-200 p-3.5">
          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
            <p className="text-[11px] uppercase tracking-[0.12em] font-bold text-stone-500 inline-flex items-center gap-1.5">
              <Receipt size={12} /> Transactions
              {txStoreScoped && <span className="normal-case tracking-normal font-semibold text-stone-800">· {homeStoreName}</span>}
            </p>
            {locationId && (
              <div className="inline-flex rounded-lg border border-stone-200 p-0.5 ml-auto" role="group" aria-label="Which stores">
                {[['This store', false], ['All stores', true]].map(([label, all]) => (
                  <button
                    key={String(label)}
                    type="button"
                    aria-pressed={txAllStores === all}
                    onClick={() => setTxAllStores(all as boolean)}
                    className={`min-h-[36px] rounded-md px-3 text-[11.5px] font-semibold ${txAllStores === all ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-50'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={openInvoice}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11.5px] font-bold
                bg-rose-600 text-white hover:bg-rose-700"
            >
              <FileText size={12} /> Check invoice
            </button>
          </div>
          {txVisible.length === 0 && (
            <p className="py-2 text-[12px] text-stone-500">No invoices placed at this store yet. Switch to All stores to see the account.</p>
          )}
          {txVisible.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-3 py-1.5 border-b border-stone-100 last:border-0">
              <span className="min-w-0">
                <span className="text-[12.5px] text-stone-800 font-medium">{chargeLabel(c)}</span>
                <span className={`ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                  c.status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                }`}>{c.status}</span>
                {/*
                  WHAT THE MONTHLY BUYS. "Monthly subscription" alone tells an
                  operator the money recurs and nothing about what for, which
                  is the question they are actually being asked to verify.
                */}
                {/* The breakdown describes a MONTHLY bill, so it may only sit
                    under a monthly invoice. It was gated on `periodStart` and
                    therefore printed "software $165.91 + hardware $136.98"
                    under a $449.99 deposits invoice — three figures that do not
                    add up, presented as though they explained the charge. */}
                {/*
                  WHAT THIS INVOICE CHARGED, from its own lines. This used to
                  print the account's whole monthly — every store's software
                  plus this order's hardware — under every invoice, a figure no
                  single invoice ever billed.
                */}
                {txAllStores && (
                  <span className="block text-[11px] font-semibold text-stone-600 mt-0.5">{storeNameOf(c)}</span>
                )}
                {(c as any).lines?.length > 0 && (
                  <span className="block text-[11px] text-stone-500 mt-0.5">
                    {((c as any).lines as Array<{ description: string | null; amount: number }>).slice(0, 3).map((l, n) => (
                      <span key={n} className="block">
                        {l.description || 'Line'} <span className="tabular-nums text-stone-400">· {money(l.amount)}</span>
                      </span>
                    ))}
                    {(c as any).lines.length > 3 && <span className="block text-stone-400">{(c as any).lines.length - 3} more lines</span>}
                  </span>
                )}
                <span className="block text-[10.5px] text-stone-400 mt-0.5">
                  {/* Stripe's own number, for looking it up there — not the headline. */}
                  <span className="font-mono">{c.number || c.id}</span>
                  <a
                    href={`${stripeBase}/invoices/${c.id}`}
                    target="_blank" rel="noopener noreferrer"
                    className="ml-2 text-stone-400 hover:text-stone-700 inline-flex items-center gap-1"
                  >
                    open in Stripe <ExternalLink size={9} />
                  </a>
                </span>
              </span>
              <span className="text-[12.5px] font-semibold text-stone-800 shrink-0">
                {money(c.amountPaid || c.amount)}
              </span>
            </div>
          ))}
          {txStoreScoped && (
            <div className="flex items-center justify-between gap-3 mt-2 pt-2 border-t border-stone-200">
              <span className="text-[12.5px] font-semibold text-stone-600">Paid to date · {homeStoreName}</span>
              <span className="text-[14px] font-bold tabular-nums text-stone-900">{money(paidAt(txVisible))}</span>
            </div>
          )}
          <div className={`flex items-center justify-between gap-3 ${txStoreScoped ? 'mt-1' : 'mt-2 pt-2 border-t border-stone-200'}`}>
            <span className={`text-[12.5px] font-semibold ${txStoreScoped ? 'text-stone-500' : 'text-stone-600'}`}>Paid to date · whole account</span>
            <span className={`tabular-nums ${txStoreScoped ? 'text-[12.5px] font-semibold text-stone-600' : 'text-[14px] font-bold text-stone-900'}`}>{money(paidCents)}</span>
          </div>
        </div>
      )}

      {/* No charges, but the account exists — still offer our own summary. */}
      {charges !== null && charges.length === 0 && !loading && (
        <button
          type="button"
          onClick={openInvoice}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-200
            text-[11.5px] font-semibold text-stone-700 hover:bg-stone-50"
        >
          <FileText size={12} /> Check invoice
        </button>
      )}

      {/* Stripe is the ledger; this screen never pretends to be it */}
      <div className="flex flex-wrap gap-2">
        {customerId && (
          <a
            href={`${stripeBase}/customers/${customerId}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-200 text-[11.5px] font-semibold text-stone-700 hover:bg-stone-50"
          >
            <CreditCard size={12} /> Customer in Stripe <ExternalLink size={10} />
          </a>
        )}
        {subscriptionId && (
          <a
            href={`${stripeBase}/subscriptions/${subscriptionId}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-200 text-[11.5px] font-semibold text-stone-700 hover:bg-stone-50"
          >
            Subscription <ExternalLink size={10} />
          </a>
        )}
        {!customerId && !subscriptionId && !loading && (
          <p className="text-[11.5px] text-amber-700 inline-flex items-center gap-1.5">
            <AlertTriangle size={12} /> No Stripe ids on this tenant yet — check the payment in Stripe by email.
          </p>
        )}
      </div>

      {/* The decision */}
      {completion ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-3.5">
          <p className="text-[12.5px] text-stone-700 inline-flex items-center gap-1.5">
            <ShieldCheck size={13} className="text-emerald-600" />
            Verified by <b className="text-stone-900">{completion.name}</b>
            {completion.roleId ? ` · ${completion.roleId}` : ''} · {when(completion.at)}
          </p>
          <button
            onClick={undo}
            disabled={busy}
            className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 bg-white text-[11px] font-semibold text-stone-600 hover:bg-stone-50 disabled:opacity-50"
          >
            {busy ? <RubyGemMark size={11} drawing color="currentColor" /> : <RotateCcw size={11} />} Withdraw
          </button>
          <p className="text-[11px] text-stone-500 mt-2">
            Withdrawing writes a new entry — it never erases who verified it, or when.
          </p>
        </div>
      ) : (
        <div>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Anything worth recording — reference, what you checked"
            className="w-full px-3.5 py-2.5 rounded-lg border border-stone-200 text-[12.5px] text-stone-800 focus:outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-500/10 mb-2"
          />
          <button
            onClick={verify}
            disabled={busy || !journeyId}
            title={journeyId ? undefined : 'The journey record is still loading'}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold min-h-[38px] disabled:opacity-50"
          >
            {busy ? <RubyGemMark size={13} drawing color="currentColor" /> : <ShieldCheck size={13} />}
            I have checked this payment
          </button>
          <p className="text-[11px] text-stone-500 mt-2 leading-relaxed">
            Recorded against your name and role, with the time. Stripe saying "paid" is not the same fact as
            somebody having looked.
          </p>
        </div>
      )}
    </div>
  );
};

export default PaymentVerifyPanel;
